import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Linkedin, Instagram, Twitter } from 'lucide-react';
import { companyInfo } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { productCategories } from '../mock-products';

// Social media links
const socialLinks = [
  { name: 'Facebook', icon: Facebook, url: 'https://facebook.com/guritamandalapersada/', color: 'hover:bg-[#1877F2]' },
  { name: 'Instagram', icon: Instagram, url: 'https://instagram.com/guritamandalapersada/', color: 'hover:bg-[#E4405F]' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://linkedin.com/company/pt-gurita-mandala-persada/', color: 'hover:bg-[#0A66C2]' },
  { name: 'X (Twitter)', icon: Twitter, url: 'https://x.com/@GuritaMandala', color: 'hover:bg-black' },
  { name: 'Email', icon: Mail, url: 'mailto:info@guritamandala.co.id', color: 'hover:bg-[#CF2E2E]' }
];

const Footer = () => {
  const { t, language } = useLanguage();

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg">
                <img
                  src="/img/logo.png"
                  alt="PT. Gurita Mandala Persada Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-white font-bold text-lg">
                {companyInfo.name}
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center transition-all ${social.color}`}
                    title={social.name}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:text-white transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm hover:text-white transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-sm hover:text-white transition-colors">
                  {t('nav.products')}
                </Link>
              </li>
              <li>
                <Link to="/industries" className="text-sm hover:text-white transition-colors">
                  {t('nav.industries')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm hover:text-white transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">{t('footer.productsTitle')}</h3>
            <ul className="space-y-2">
              {productCategories.map((category) => (
                <li key={category.slug}>
                  <Link
                    to={`/products/${category.slug}`}
                    className="text-sm hover:text-white transition-colors hover:translate-x-1 inline-block"
                  >
                    {language === 'id' ? category.nameId : category.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">
              {t('footer.contactUs')}
            </h3>

            <ul className="space-y-3">
              {/* Address - Google Maps */}
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="mt-1 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/n32EJXrcHHy8ukyNA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm hover:underline"
                >
                  {companyInfo.address}
                </a>
              </li>

              {/* Phone */}
              {companyInfo.phone && (
                <li className="flex items-center space-x-3">
                  <Phone size={18} className="flex-shrink-0" />
                  <a
                    href={`tel:${companyInfo.phone.replace(/[^\d+]/g, '')}`}
                    className="text-sm hover:underline"
                  >
                    {companyInfo.phone}
                  </a>
                </li>
              )}

              {/* Phone 1 */}
              {companyInfo.phone1 && (
                <li className="flex items-center space-x-3">
                  <Phone size={18} className="flex-shrink-0" />
                  <a
                    href={`tel:${companyInfo.phone1.replace(/[^\d+]/g, '')}`}
                    className="text-sm hover:underline"
                  >
                    {companyInfo.phone1}
                  </a>
                </li>
              )}

              {/* Phone 2 */}
              {companyInfo.phone2 && (
                <li className="flex items-center space-x-3">
                  <Phone size={18} className="flex-shrink-0" />
                  <a
                    href={`tel:${companyInfo.phone2.replace(/[^\d+]/g, '')}`}
                    className="text-sm hover:underline"
                  >
                    {companyInfo.phone2}
                  </a>
                </li>
              )}

              {/* Email */}
              {companyInfo.email && (
                <li className="flex items-center space-x-3">
                  <Mail size={18} className="flex-shrink-0" />
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="text-sm hover:underline"
                  >
                    {companyInfo.email}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} {t('footer.copyright')}
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-sm text-gray-500 hover:text-white transition-colors">
                {t('footer.privacyPolicy')}
              </a>
              <a href="#" className="text-sm text-gray-500 hover:text-white transition-colors">
                {t('footer.termsOfService')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;